import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";
import {
  createAdminSector,
  deleteAdminSector,
  deleteAdminSectorGalleryImage,
  deleteAdminSectorMainImage,
  deleteAdminSectorProjectImage,
  getAdminSectors,
  updateAdminSector,
  uploadAdminSectorGalleryImage,
  uploadAdminSectorMainImage,
  uploadAdminSectorProjectImage,
} from "../../../api/sectorsAdmin";

const EMPTY_BENEFITS = {
  interiorWalls: [""],
  ceilingSystems: [""],
  exteriorCladding: [""],
};

const EMPTY_PROJECT = {
  title: "",
  description: "",
  image: "",
};

const EMPTY_FORM = {
  slug: "",
  language: "en",
  title: "",
  subtitle: "",
  description: "",
  about: "",
  image: "",
  benefits: { ...EMPTY_BENEFITS },
  project: { ...EMPTY_PROJECT },
  galleryImages: [""],
  isActive: true,
};

const mapSectorToForm = (sector) => ({
  slug: sector?.slug || "",
  language: String(sector?.language || "en").toLowerCase().split("-")[0],
  title: sector?.title || "",
  subtitle: sector?.subtitle || "",
  description: sector?.description || "",
  about: sector?.about || "",
  image: sector?.image || "",
  benefits: {
    interiorWalls:
      Array.isArray(sector?.benefits?.interiorWalls) && sector.benefits.interiorWalls.length
        ? sector.benefits.interiorWalls
        : [""],
    ceilingSystems:
      Array.isArray(sector?.benefits?.ceilingSystems) && sector.benefits.ceilingSystems.length
        ? sector.benefits.ceilingSystems
        : [""],
    exteriorCladding:
      Array.isArray(sector?.benefits?.exteriorCladding) && sector.benefits.exteriorCladding.length
        ? sector.benefits.exteriorCladding
        : [""],
  },
  project: {
    title: sector?.project?.title || "",
    description: sector?.project?.description || "",
    image: sector?.project?.image || "",
  },
  galleryImages:
    Array.isArray(sector?.galleryImages) && sector.galleryImages.length
      ? sector.galleryImages
      : Array.isArray(sector?.gallery) && sector.gallery.length
      ? sector.gallery.map((g) => g?.imageUrl || "").filter(Boolean)
      : [""],
  isActive: typeof sector?.isActive === "boolean" ? sector.isActive : true,
});

const toPayload = (form) => ({
  slug: String(form.slug || "").trim(),
  language: String(form.language || "en").toLowerCase(),
  title: String(form.title || "").trim(),
  subtitle: String(form.subtitle || "").trim(),
  description: String(form.description || "").trim(),
  about: String(form.about || "").trim(),
  image: String(form.image || "").trim() || null,
  benefits: {
    interiorWalls: (form.benefits?.interiorWalls || []).map((x) => String(x || "").trim()).filter(Boolean),
    ceilingSystems: (form.benefits?.ceilingSystems || []).map((x) => String(x || "").trim()).filter(Boolean),
    exteriorCladding: (form.benefits?.exteriorCladding || []).map((x) => String(x || "").trim()).filter(Boolean),
  },
  project: {
    title: String(form.project?.title || "").trim(),
    description: String(form.project?.description || "").trim(),
    image: String(form.project?.image || "").trim() || null,
  },
  galleryImages: (form.galleryImages || []).map((x) => String(x || "").trim()).filter(Boolean),
  isActive: Boolean(form.isActive),
});

const SectorsManager = () => {
  const { i18n } = useTranslation();
  const { notify } = useNotification();
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createMainFile, setCreateMainFile] = useState(null);
  const [createProjectFile, setCreateProjectFile] = useState(null);
  const [createGalleryFiles, setCreateGalleryFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editMainFile, setEditMainFile] = useState(null);
  const [editProjectFile, setEditProjectFile] = useState(null);
  const [editGalleryFiles, setEditGalleryFiles] = useState([]);

  const loadSectors = async () => {
    setLoading(true);
    try {
      const data = await getAdminSectors();
      setSectors(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load sectors",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSectors();
  }, []);

  const sortedSectors = useMemo(() => {
    const lang = String(i18n.language || "en").toLowerCase().split("-")[0];
    return sectors
      .filter((s) => String(s?.language || "en").toLowerCase().split("-")[0] === lang)
      .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  }, [sectors, i18n.language]);

  const handleCreate = async () => {
    if (!createForm.slug.trim() || !createForm.title.trim()) {
      notify({ type: "error", title: "Slug and title are required" });
      return;
    }
    setCreating(true);
    try {
      const created = await createAdminSector(toPayload(createForm));
      const createdId = created?.id;
      if (createdId) {
        if (createMainFile) await uploadAdminSectorMainImage({ id: createdId, file: createMainFile });
        if (createProjectFile) await uploadAdminSectorProjectImage({ id: createdId, file: createProjectFile });
        for (const file of createGalleryFiles) {
          await uploadAdminSectorGalleryImage({ id: createdId, file });
        }
      }
      notify({ type: "success", title: "Sector created" });
      setCreateForm(EMPTY_FORM);
      setCreateMainFile(null);
      setCreateProjectFile(null);
      setCreateGalleryFiles([]);
      await loadSectors();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (sector) => {
    setEditingId(sector.id);
    setEditForm(mapSectorToForm(sector));
    setEditMainFile(null);
    setEditProjectFile(null);
    setEditGalleryFiles([]);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editForm.slug.trim() || !editForm.title.trim()) {
      notify({ type: "error", title: "Slug and title are required" });
      return;
    }
    try {
      await updateAdminSector({ id: editingId, payload: toPayload(editForm) });
      if (editMainFile) await uploadAdminSectorMainImage({ id: editingId, file: editMainFile });
      if (editProjectFile) await uploadAdminSectorProjectImage({ id: editingId, file: editProjectFile });
      for (const file of editGalleryFiles) {
        await uploadAdminSectorGalleryImage({ id: editingId, file });
      }
      notify({ type: "success", title: "Sector updated" });
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      setEditMainFile(null);
      setEditProjectFile(null);
      setEditGalleryFiles([]);
      await loadSectors();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const handleDelete = async (sector) => {
    const ok = window.confirm(`Delete sector "${sector?.title || sector?.slug}"?`);
    if (!ok) return;
    try {
      await deleteAdminSector({ id: sector.id });
      notify({ type: "success", title: "Sector deleted" });
      await loadSectors();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const removeMainImage = async (id) => {
    try {
      await deleteAdminSectorMainImage({ id });
      notify({ type: "success", title: "Main image removed" });
      await loadSectors();
    } catch (error) {
      notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const removeProjectImage = async (id) => {
    try {
      await deleteAdminSectorProjectImage({ id });
      notify({ type: "success", title: "Project image removed" });
      await loadSectors();
    } catch (error) {
      notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const removeGalleryImage = async ({ id, imageId }) => {
    try {
      await deleteAdminSectorGalleryImage({ id, imageId });
      notify({ type: "success", title: "Gallery image removed" });
      await loadSectors();
    } catch (error) {
      notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const updateBenefitList = (setForm, key, idx, value) => {
    setForm((prev) => {
      const nextList = [...(prev.benefits?.[key] || [""])];
      nextList[idx] = value;
      return {
        ...prev,
        benefits: { ...prev.benefits, [key]: nextList },
      };
    });
  };

  const renderForm = ({ form, setForm, mainFile, setMainFile, projectFile, setProjectFile, galleryFiles, setGalleryFiles, isEdit = false, sectorId = null }) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700">
        Slug
        <input type="text" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">
        Language
        <select value={form.language} onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
          <option value="en">EN</option>
          <option value="ar">AR</option>
          <option value="ku">KU</option>
        </select>
      </label>
      <label className="text-sm text-slate-700">
        Title
        <input type="text" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">
        Subtitle
        <input type="text" value={form.subtitle} onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Description
        <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        About
        <textarea value={form.about} onChange={(e) => setForm((p) => ({ ...p, about: e.target.value }))} className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Main image URL
        <input type="text" value={form.image} onChange={(e) => setForm((p) => ({ ...p, image: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">
        Main image file
        <input type="file" accept="image/*" onChange={(e) => setMainFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" />
      </label>
      <label className="text-sm text-slate-700">
        Project image file
        <input type="file" accept="image/*" onChange={(e) => setProjectFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" />
      </label>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Benefits</p>
        {["interiorWalls", "ceilingSystems", "exteriorCladding"].map((key) => (
          <div key={key} className="mb-2">
            <p className="text-xs uppercase text-slate-500 mb-1">{key}</p>
            {(form.benefits?.[key] || [""]).map((line, idx) => (
              <div key={`${key}-${idx}`} className="flex gap-2 mb-1">
                <input
                  type="text"
                  value={line}
                  onChange={(e) => updateBenefitList(setForm, key, idx, e.target.value)}
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      benefits: {
                        ...prev.benefits,
                        [key]: (prev.benefits?.[key] || []).filter((_, i) => i !== idx),
                      },
                    }))
                  }
                  className="px-2 rounded border border-rose-200 text-rose-700"
                >
                  -
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  benefits: {
                    ...prev.benefits,
                    [key]: [...(prev.benefits?.[key] || []), ""],
                  },
                }))
              }
              className="text-xs px-2 py-1 rounded border border-slate-300"
            >
              Add line
            </button>
          </div>
        ))}
      </div>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Project Block</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <input type="text" value={form.project?.title || ""} onChange={(e) => setForm((p) => ({ ...p, project: { ...p.project, title: e.target.value } }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Project title" />
          <input type="text" value={form.project?.image || ""} onChange={(e) => setForm((p) => ({ ...p, project: { ...p.project, image: e.target.value } }))} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Project image URL" />
          <textarea value={form.project?.description || ""} onChange={(e) => setForm((p) => ({ ...p, project: { ...p.project, description: e.target.value } }))} className="sm:col-span-2 rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[70px]" placeholder="Project description" />
        </div>
      </div>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Gallery URLs</p>
        {(form.galleryImages || [""]).map((url, idx) => (
          <div key={`g-${idx}`} className="flex gap-2 mb-1">
            <input
              type="text"
              value={url}
              onChange={(e) =>
                setForm((prev) => {
                  const next = [...(prev.galleryImages || [""])];
                  next[idx] = e.target.value;
                  return { ...prev, galleryImages: next };
                })
              }
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button type="button" onClick={() => setForm((prev) => ({ ...prev, galleryImages: (prev.galleryImages || []).filter((_, i) => i !== idx) }))} className="px-2 rounded border border-rose-200 text-rose-700">-</button>
          </div>
        ))}
        <button type="button" onClick={() => setForm((prev) => ({ ...prev, galleryImages: [...(prev.galleryImages || []), ""] }))} className="text-xs px-2 py-1 rounded border border-slate-300">Add gallery URL</button>
      </div>

      <label className="text-sm text-slate-700 sm:col-span-2">
        Upload gallery image(s)
        <input type="file" multiple accept="image/*" onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))} className="mt-1 block w-full text-sm" />
        {galleryFiles?.length ? <span className="mt-1 block text-xs text-slate-500">{galleryFiles.length} file(s) selected</span> : null}
      </label>

      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={Boolean(form.isActive)} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
        Active
      </label>

      {isEdit && sectorId ? (
        <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Current Images</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="rounded border p-2">
              <p className="text-xs text-slate-500 mb-1">Main</p>
              {resolveMediaUrl(form.image) ? <img src={resolveMediaUrl(form.image)} alt="" className="w-full h-24 object-cover rounded" /> : <div className="w-full h-24 bg-slate-100 rounded" />}
              <button type="button" onClick={() => removeMainImage(sectorId)} className="mt-2 text-xs px-2 py-1 rounded border border-rose-200 text-rose-700">Delete main</button>
            </div>
            <div className="rounded border p-2">
              <p className="text-xs text-slate-500 mb-1">Project</p>
              {resolveMediaUrl(form.project?.image) ? <img src={resolveMediaUrl(form.project?.image)} alt="" className="w-full h-24 object-cover rounded" /> : <div className="w-full h-24 bg-slate-100 rounded" />}
              <button type="button" onClick={() => removeProjectImage(sectorId)} className="mt-2 text-xs px-2 py-1 rounded border border-rose-200 text-rose-700">Delete project</button>
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-4 gap-2">
            {(sectors.find((s) => s.id === sectorId)?.gallery || []).map((img) => (
              <div key={img.id} className="rounded border p-2">
                {resolveMediaUrl(img?.imageUrl) ? <img src={resolveMediaUrl(img?.imageUrl)} alt="" className="w-full h-20 object-cover rounded" /> : null}
                <button type="button" onClick={() => removeGalleryImage({ id: sectorId, imageId: img.id })} className="mt-1 text-xs px-2 py-1 rounded border border-rose-200 text-rose-700">Delete</button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Sectors Manager</h2>
            <p className="text-sm text-slate-500 mt-1">CRUD sectors, benefits, project block and gallery images.</p>
          </div>
          <button type="button" onClick={loadSectors} className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm">Reload</button>
        </div>

        {renderForm({
          form: createForm,
          setForm: setCreateForm,
          mainFile: createMainFile,
          setMainFile: setCreateMainFile,
          projectFile: createProjectFile,
          setProjectFile: setCreateProjectFile,
          galleryFiles: createGalleryFiles,
          setGalleryFiles: setCreateGalleryFiles,
        })}

        <div className="mt-3 flex justify-end">
          <button type="button" disabled={creating} onClick={handleCreate} className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60">
            {creating ? "Creating..." : "Create sector"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Slug</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Lang</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Title</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Image</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">Loading sectors...</td></tr>
            ) : sortedSectors.length ? (
              sortedSectors.map((item) => (
                <tr key={item.id} className="border-b border-slate-200 align-top">
                  <td className="px-3 py-3 text-sm">{item.id}</td>
                  <td className="px-3 py-3 text-sm">{item.slug}</td>
                  <td className="px-3 py-3 text-sm uppercase">{item.language}</td>
                  <td className="px-3 py-3 text-sm font-medium">{item.title}</td>
                  <td className="px-3 py-3">
                    {resolveMediaUrl(item.image) ? <img src={resolveMediaUrl(item.image)} alt="" className="w-24 h-16 object-cover rounded border border-slate-200" /> : <p className="text-xs text-slate-500">No image</p>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-2">
                      <button type="button" onClick={() => startEdit(item)} className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10">Edit</button>
                      <button type="button" onClick={() => handleDelete(item)} className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">No sectors found for this language.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Sector #{editingId}</h3>
            <button type="button" onClick={() => { setEditingId(null); setEditForm(EMPTY_FORM); setEditMainFile(null); setEditProjectFile(null); setEditGalleryFiles([]); }} className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">Close</button>
          </div>

          {renderForm({
            form: editForm,
            setForm: setEditForm,
            mainFile: editMainFile,
            setMainFile: setEditMainFile,
            projectFile: editProjectFile,
            setProjectFile: setEditProjectFile,
            galleryFiles: editGalleryFiles,
            setGalleryFiles: setEditGalleryFiles,
            isEdit: true,
            sectorId: editingId,
          })}

          <div className="mt-3 flex justify-end">
            <button type="button" onClick={saveEdit} className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm">Save changes</button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SectorsManager;
